import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../ThemeContext';
import Button from '../../../common/button/Button';
import { CustomDialog } from '@voicecare.ai/ui-library';
import { validateCustomQuestions, createCustomQuestions } from '../../../../utils/apiClient';
import './AddQuestion.css';
import { message } from 'antd';

const EXPECTED_VALUE_OPTIONS = [
  {
    value: 'Date',
    label: 'Date',
    description: 'For all date-related fields (e.g., service date, birth date)'
  },
  {
    value: 'Currency',
    label: 'Currency',
    description: 'For monetary amounts (e.g., claim amount, copay)'
  },
  {
    value: 'Percentage',
    label: 'Percentage',
    description: 'For values like co-insurance or coverage percentage'
  },
  {
    value: 'Days',
    label: 'Days',
    description: 'For time periods (e.g., authorization duration)'
  },
  {
    value: 'Text',
    label: 'Text',
    description: 'For free-form explanations or descriptions'
  },
  {
    value: 'Alphanumeric',
    label: 'Alphanumeric',
    description: 'For claim numbers and similar IDs'
  },
  {
    value: 'Numeric ID',
    label: 'Numeric ID',
    description: 'For payor IDs and numeric identifiers'
  },
  {
    value: 'Address',
    label: 'Address',
    description: 'For physical addresses and locations'
  },
  {
    value: 'Phone Number',
    label: 'Phone Number',
    description: 'For contact numbers and fax numbers'
  },
  {
    value: 'Text',
    label: 'Yes/No',
    description: 'For boolean responses and confirmations'
  }
];

const AddQuestion = ({ isOpen, onClose, onSave, selectedBranch, selectedUsecase, selectedSubUsecase }) => {
  const { themeMode, currentTheme } = useTheme();
  const [question, setQuestion] = useState('');
  const [selectedExpectedValue, setSelectedExpectedValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const [questionsList, setQuestionsList] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [currentValidationResult, setCurrentValidationResult] = useState(null);
  const [suggestionCount, setSuggestionCount] = useState(0);

  // Update validateAndAddQuestion to accept an optional count parameter
  const validateAndAddQuestion = async (questionText, count = suggestionCount) => {
    if (!questionText.trim() || !selectedExpectedValue) return;

    setIsValidating(true);
    try {
      const validationResponse = await validateCustomQuestions({
        questions: [{
          question: questionText.trim(),
          id: Date.now().toString()
        }],
        branch: selectedBranch,
        usecase: selectedUsecase,
        subusecase: selectedSubUsecase
      });

      const validationResult = validationResponse.questions[0];
      
      if (validationResult.is_valid === "True") {
        const newQuestion = {
          Question: validationResult.suggested_question || questionText.trim(),
          "Expected Value": selectedExpectedValue,
          id: Date.now().toString(),
          isValid: true,
          isExisting: validationResult.is_existing === "True",
          isValidated: true
        };
        setQuestionsList([...questionsList, newQuestion]);
        setQuestion('');
        setSelectedExpectedValue('');
        setIsDropdownOpen(false);
        setSuggestionCount(0); // reset on success
      } else {
        if (count >= 2) {
          message.error("Try again with other question");
          setQuestion('');
          setSelectedExpectedValue('');
          setIsDropdownOpen(false);
          setSuggestionCount(0);
          setShowValidationDialog(false);
          setCurrentValidationResult(null);
        } else {
          setCurrentValidationResult(validationResult);
          setShowValidationDialog(true);
        }
      }
    } catch (error) {
      console.error('Error validating question:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleAddQuestion = () => {
    validateAndAddQuestion(question);
  };

  const handleRemoveQuestion = (index) => {
    const updatedList = [...questionsList];
    updatedList.splice(index, 1);
    setQuestionsList(updatedList);
  };

  const handleSaveAll = async () => {
    if (questionsList.length === 0) return;

    setIsSaving(true);
    try {
      const path = `${selectedBranch}-${selectedUsecase}${selectedSubUsecase ? `-${selectedSubUsecase}` : ''}`;
      
      // Format questions for creation
      const questionsToCreate = questionsList.map(q => ({
        question: q.Question,
        expected_value: q["Expected Value"]
      }));

      const createResponse = await createCustomQuestions({
        path,
        questions: questionsToCreate
      });

      if (createResponse.question_ids && createResponse.question_ids.length > 0) {
        // Transform created questions to match the expected format
        const createdQuestions = questionsList.map((q, index) => ({
          id: createResponse.question_ids[index],
          Question: q.Question,
          "Expected Value": q["Expected Value"],
          type: "custom",
          path
        }));

        onSave(createdQuestions);
        handleClose();
      }
    } catch (error) {
      console.error('Error saving questions:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setQuestion('');
    setSelectedExpectedValue('');
    setIsDropdownOpen(false);
    setQuestionsList([]);
    setValidationResults({});
    setCurrentValidationResult(null);
    setShowValidationDialog(false);
    onClose();
  };

  const hasValidatedQuestions = questionsList.some(q => q.isValidated);

  const handleValidationConfirm = () => {
    if (currentValidationResult?.suggested_question) {
      // Update the question input with suggested text
      setQuestion(currentValidationResult.suggested_question);
      // Increment suggestionCount before validation
      setSuggestionCount(prev => {
        const newCount = prev + 1;
        validateAndAddQuestion(currentValidationResult.suggested_question, newCount);
        return newCount;
      });
      setShowValidationDialog(false);
      setCurrentValidationResult(null);
    }
  };

  const handleValidationCancel = () => {
    // Clear the question and expected value
    setQuestion('');
    setSelectedExpectedValue('');
    setShowValidationDialog(false);
    setCurrentValidationResult(null);
    setSuggestionCount(0);
  };

  if (!isOpen) return null;

  return (
    <div className={`add-question-container ${themeMode}`}>
      <div 
        className="add-question-content"
        style={{
          background: currentTheme.settingPage.popupBackground,
          color: currentTheme.settingPage.textColor
        }}
      >
        <div className="add-question-header">
          <h2>Add New Questions</h2>
          <button 
            className="close-button"
            onClick={handleClose}
            style={{ color: currentTheme.settingPage.textColor }}
          >
            {themeMode === "dark" ? (
              <img src="/icons/cancel-grey.svg" width={"20vh"} />
            ) : (
              <img src="/icons/cancel-orange.svg" width={"20vh"} />
            )}
          </button>
        </div>

        <div className="add-question-form">
          <div className="form-group">
            <label>Question:</label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter your question..."
              style={{
                background: currentTheme.settingPage.themeBackground,
                color: currentTheme.settingPage.textColor,
                border: `1px solid ${currentTheme.settingPage.button}`
              }}
            />
          </div>

          <div className="form-group">
            <label>Expected Value Type:</label>
            <div className="custom-dropdown">
              <div 
                className="dropdown-header"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{
                  background: currentTheme.settingPage.themeBackground,
                  color: currentTheme.settingPage.textColor,
                  border: `1px solid ${currentTheme.settingPage.button}`
                }}
              >
                {selectedExpectedValue || 'Select expected value type'}
                <span className={`arrow ${isDropdownOpen ? 'up' : 'down'}`}></span>
              </div>
              
              {isDropdownOpen && (
                <div 
                  className="dropdown-options"
                  style={{
                    background: currentTheme.settingPage.themeBackground,
                    border: `1px solid ${currentTheme.settingPage.button}`
                  }}
                >
                  {EXPECTED_VALUE_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className="option-container"
                      onClick={() => {
                        setSelectedExpectedValue(option.value);
                        setIsDropdownOpen(false);
                      }}
                      onMouseEnter={() => setHoveredOption(option)}
                      onMouseLeave={() => setHoveredOption(null)}
                      style={{
                        background: selectedExpectedValue === option.value 
                          ? currentTheme.settingPage.button
                          : 'transparent'
                      }}
                    >
                      <div className="option-label">{option.label}</div>
                      {hoveredOption === option && (
                        <div 
                          className="option-tooltip"
                          style={{
                            background: currentTheme.settingPage.button,
                            color: '#ffffff'
                          }}
                        >
                          {option.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <Button
              label={isValidating ? "Validating..." : "Add to List"}
              backgroundColor={currentTheme.settingPage.button}
              color="#ffffff"
              width="120px"
              borderRadius="5px"
              height="4vh"
              onClick={handleAddQuestion}
              disabled={!question.trim() || !selectedExpectedValue || isValidating}
            />
          </div>
        </div>

        {questionsList.length > 0 && (
          <div className="questions-list-container">
            <div className="questions-list-header">
              <h3>Questions to Add ({questionsList.length})</h3>
              {!hasValidatedQuestions && (
                <div className="validation-note" style={{ color: '#ff9800', fontSize: '0.9em', marginTop: '5px' }}>
                  Add and validate questions to enable saving
                </div>
              )}
            </div>
            <div className="questions-list">
              {questionsList.map((q, index) => (
                <div 
                  key={index} 
                  className="question-item"
                  style={{
                    background: currentTheme.settingPage.themeBackground,
                    border: `1px solid ${currentTheme.settingPage.button}`
                  }}
                >
                  <div className="question-content">
                    <div className="question-text">
                      {q.Question}
                      {q.isValidated && (
                        <span className="validation-badge" style={{ 
                          color: '#4caf50',
                          marginLeft: '8px',
                          fontSize: '0.9em'
                        }}>
                          ✓ Validated
                        </span>
                      )}
                    </div>
                    <div className="question-expected">Expected: {q["Expected Value"]}</div>
                    {q.isExisting && (
                      <div className="question-note" style={{ color: '#ff9800' }}>
                        Note: This question already exists
                      </div>
                    )}
                  </div>
                  <div className="question-actions">
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveQuestion(index)}
                      style={{ color: currentTheme.settingPage.textColor }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="add-question-footer">
          <div className="footer-left">
            <Button
              label="Reset"
              backgroundColor={currentTheme.settingPage.themeBackground}
              color={currentTheme.settingPage.textColor}
              width="120px"
              borderRadius="5px"
              height="4vh"
              onClick={() => {
                setQuestion('');
                setSelectedExpectedValue('');
                setIsDropdownOpen(false);
              }}
              style={{
                border: `1px solid ${currentTheme.settingPage.button}`
              }}
            />
          </div>
          <div className="footer-right">
            <Button
              label={isSaving ? "Saving..." : "Save All Questions"}
              backgroundColor={currentTheme.settingPage.button}
              color="#ffffff"
              width="150px"
              borderRadius="5px"
              height="4vh"
              onClick={handleSaveAll}
              disabled={!hasValidatedQuestions || isSaving}
              style={{
                opacity: !hasValidatedQuestions ? 0.6 : 1,
                cursor: !hasValidatedQuestions ? 'not-allowed' : 'pointer'
              }}
            />
            {!hasValidatedQuestions && questionsList.length > 0 && (
              <div className="save-button-tooltip" style={{ 
                color: '#ff9800',
                fontSize: '0.9em',
                marginTop: '5px',
                textAlign: 'center'
              }}>
                Add and validate questions to enable saving
              </div>
            )}
          </div>
        </div>
      </div>

      <CustomDialog
        open={showValidationDialog}
        onClose={handleValidationCancel}
        title="Question Validation"
        message={
          currentValidationResult ? (
            <div>
              <p><strong>Original Question:</strong> {currentValidationResult.original_question}</p>
              <p><strong>Suggested Question:</strong> {currentValidationResult.suggested_question}</p>
              <p><strong>Status:</strong> {currentValidationResult.is_valid === "True" ? "Valid" : "Invalid"}</p>
              {currentValidationResult.is_existing === "True" && (
                <p><strong>Note:</strong> This question already exists.</p>
              )}
            </div>
          ) : "Validating question..."
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleValidationConfirm}
        onCancel={handleValidationCancel}
        themeMode={themeMode}
        currentTheme={currentTheme}
      />
    </div>
  );
};

export default AddQuestion; 